import React, { useCallback, useEffect, useRef, useState } from 'react';
import Popup from '@atlaskit/popup';
import { fg } from '@atlassian/jira-feature-gating';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import {
	Status,
	type MigrationId,
	type MigrationStatus,
} from '@atlassian/jira-project-create-importer-common';
import { EXPERIENCES } from '../../../common/constants/metrics';
import { useTrackMigrationStatus } from '../../../common/utils/use-track-migration-status';
import { PopoverContent } from './popover-content';
import { useButtonVariation } from './use-button-variation';

type Props = {
	migrationId: MigrationId;
	onViewedPostCompletion: () => void;
	onDownloadClick: () => void;
	onSuccess: (migrationStatus: MigrationStatus) => void;
	onFail: (migrationStatus: MigrationStatus) => void;
};

export const ProgressButton = ({
	migrationId,
	onViewedPostCompletion,
	onDownloadClick,
	onSuccess,
	onFail,
}: Props) => {
	const { migrationStatus } = useTrackMigrationStatus(
		EXPERIENCES.IMPORT_PART_JSW,
		migrationId,
		fg('enable-x2c-standardised-flow') ? onSuccess : undefined,
		fg('enable-x2c-standardised-flow') ? onFail : undefined,
	);
	const { overallStatus } = migrationStatus;
	const [isOpen, setIsOpen] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const timeout = useRef<NodeJS.Timeout | undefined>(undefined);

	/*
	 * immediately opening popover positioning it on top left
	 * delaying to position it properly.
	 */
	useEffect(() => {
		if (overallStatus === Status.IN_PROGRESS) {
			timeout.current = setTimeout(() => {
				overallStatus === Status.IN_PROGRESS && setIsOpen(true);
			}, 1000);
		}
		return () => {
			timeout.current && clearTimeout(timeout.current);
		};
	}, [overallStatus]);

	const { title, leftIcon, rightIcon, ButtonWrapper } = useButtonVariation(
		migrationStatus.overallStatus,
		isOpen,
	);

	const onButtonClick = useCallback(() => {
		if (!isOpen && (overallStatus === Status.FAILED || overallStatus === Status.SUCCESS)) {
			onViewedPostCompletion();
		}
		setIsOpen(!isOpen);
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			}),
			'postImportProgressButton',
			{ status: overallStatus },
		);
		timeout.current && clearTimeout(timeout.current);
	}, [createAnalyticsEvent, isOpen, onViewedPostCompletion, overallStatus]);

	if (fg('enable-x2c-standardised-flow')) {
		return null;
	}

	return (
		<Popup
			testId="project-create-import-progress.ui.import-progress-button.progress-button.popup"
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			placement="bottom-start"
			content={() => (
				<PopoverContent migrationStatus={migrationStatus} onDownloadClick={onDownloadClick} />
			)}
			trigger={(triggerProps) => (
				<ButtonWrapper
					{...triggerProps}
					appearance="primary"
					isSelected={isOpen}
					iconBefore={leftIcon}
					iconAfter={rightIcon}
					spacing="compact"
					onClick={onButtonClick}
				>
					{title}
				</ButtonWrapper>
			)}
		/>
	);
};
